import { ArrowDown, ArrowUp } from 'lucide-react';
import { type ReactNode, useCallback, useState } from 'react';
import type { Header, HeaderGroup } from '@tanstack/react-table';
import { flexRender } from '@tanstack/react-table';

import {
  cn,
  DeviceTypeDetectionVariant,
  TableHead,
  TableHeader,
  TableRow,
  useDeviceTypeDetection,
} from '@crossnokaye/ux-common';

import { FilterTrigger } from '@/components/data-table/features/filtering';

import { getPinnedCellStyles } from './pinned-column-styles';
import { useTable } from '../context';

// =============================================================================
// Types
// =============================================================================

interface DataTableHeadProps<TData> {
  headerGroups: HeaderGroup<TData>[];
  stickyHeader: boolean;
}

interface HeaderCellProps<TData> {
  header: Header<TData, unknown>;
}

// =============================================================================
// Resize Handle
// =============================================================================

interface ResizeHandleProps<TData> {
  header: Header<TData, unknown>;
  resetSize?: () => void;
}

const ResizeHandle = <TData,>({ header, resetSize }: ResizeHandleProps<TData>): ReactNode => {
  const [isResizeHovered, setIsResizeHovered] = useState(false);
  const { isMobile } = useDeviceTypeDetection({
    variant: DeviceTypeDetectionVariant.DeviceOnly,
  });

  if (!header.column.getCanResize()) {
    return null;
  }

  const isResizing = header.column.getIsResizing();
  const resizeHandler = header.getResizeHandler();

  return (
    <div
      data-testid="resize-handle"
      onDoubleClick={() => resetSize?.()}
      onMouseEnter={() => setIsResizeHovered(true)}
      onMouseLeave={() => setIsResizeHovered(false)}
      onMouseDown={(e) => {
        e.stopPropagation();
        resizeHandler(e);
      }}
      onTouchStart={(e) => {
        e.stopPropagation();
        resizeHandler(e as unknown as React.MouseEvent<HTMLDivElement, MouseEvent>);
      }}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      className="absolute top-[0.0625rem] h-full bg-popover bg-opacity-50 select-none touch-none right-0"
      style={{
        background:
          isResizing || isResizeHovered
            ? 'hsl(var(--primary))'
            : isMobile
              ? 'hsl(var(--border))'
              : 'hsl(var(--border))',
        opacity: isResizing || isResizeHovered ? 1 : isMobile ? 0.3 : 0,
        transition: 'opacity 300ms',
        width: isMobile ? '0.5rem' : '0.25rem',
        cursor: 'col-resize',
        ...(isMobile && {
          borderLeft: '0.0625rem solid hsl(var(--border))',
        }),
      }}
    />
  );
};

// =============================================================================
// Sort Indicator
// =============================================================================

interface SortIndicatorProps<TData> {
  header: Header<TData, unknown>;
  isHovered: boolean;
}

const SortIndicator = <TData,>({ header, isHovered }: SortIndicatorProps<TData>): ReactNode => {
  if (!header.column.getCanSort()) {
    return null;
  }

  const sortDirection = header.column.getIsSorted();

  return (
    <div className="w-4 h-4">
      {sortDirection === 'asc' ? (
        <ArrowUp className={cn('h-4 w-4 text-primary')} data-testid="arrow-up" />
      ) : sortDirection === 'desc' ? (
        <ArrowDown className={cn('h-4 w-4 text-primary')} data-testid="arrow-down" />
      ) : (
        <div
          data-testid="unsorted-up-arrow"
          data-hovered={isHovered}
          style={{
            height: '1rem',
            width: '1rem',
            opacity: isHovered ? 1 : 0,
            transition: 'opacity 300ms',
          }}
        >
          <ArrowUp className={cn('h-4 w-4')} data-testid="arrow-up" />
        </div>
      )}
    </div>
  );
};

// =============================================================================
// Header Cell
// =============================================================================

const HeaderCell = <TData,>({ header }: HeaderCellProps<TData>): ReactNode => {
  const { columnConfigs, table, styles, overlayColumnIds, overlayHeaderClassName } =
    useTable<TData>();
  const columnConfig = columnConfigs.find((c) => c.id === header.column.id);
  const [isHovered, setIsHovered] = useState(false);
  const isOverlayed = overlayColumnIds.includes(header.id);
  const overlayedHeaderClassName = isOverlayed
    ? typeof overlayHeaderClassName === 'function'
      ? overlayHeaderClassName(header.id)
      : overlayHeaderClassName
    : undefined;

  const pinnedStyles = getPinnedCellStyles(header.column, columnConfigs, {
    zIndexLeft: 21,
    zIndexRight: 2,
  });

  // Check for custom header renderer (used by SelectColumn)
  const customHeaderRenderer = header.column.columnDef.meta?.customHeaderRenderer;

  // Get header content - prefer meta.headerContent, fallback to column id
  const headerContent = header.column.columnDef.meta?.headerContent ?? header.column.id;
  const canSort = header.column.getCanSort();

  const handleMouseEnter = useCallback(() => setIsHovered(true), []);
  const handleMouseLeave = useCallback(() => setIsHovered(false), []);
  const handleSortClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (canSort && !header.column.getIsResizing() && !e.ctrlKey) {
        header.column.toggleSorting(undefined, e.shiftKey);
      }
    },
    [canSort, header.column]
  );

  // There is outstanding decisions to be made on if we should allow the custom header to overwrite the
  // core default styles of the TableHead component.
  // See discussion in this PR: https://github.com/crossnokaye/gaia/pull/565
  // after we have used customHeaders in the wild revisit this and make a decision.
  if (customHeaderRenderer) {
    return (
      <TableHead
        key={header.id}
        className={cn(
          'truncate overflow-hidden flex px-0 pt-0 relative',
          styles.header.base,
          overlayedHeaderClassName
        )}
        data-testid={`data-table-head-${header.id}`}
        data-column-id={header.id}
        style={{
          width: `calc(var(--header-${header.id}-size) * 1px)`,
          minWidth: header.column.columnDef.minSize,
          maxWidth: header.column.columnDef.maxSize,
          ...pinnedStyles,
        }}
      >
        {customHeaderRenderer(table)}
      </TableHead>
    );
  }

  return (
    <TableHead
      key={header.id}
      className={cn(
        'truncate overflow-hidden flex px-0 pt-0 relative',
        styles.header.base,
        overlayedHeaderClassName
      )}
      data-testid={`data-table-head-${header.id}`}
      data-column-id={header.id}
      style={{
        width: `calc(var(--header-${header.id}-size) * 1px)`,
        minWidth: header.column.columnDef.minSize,
        maxWidth: header.column.columnDef.maxSize,
        ...pinnedStyles,
      }}
    >
      <div
        className={cn(
          'flex flex-row justify-between items-center gap-x-1.5 px-2.5 pt-0.5 w-full transition-colors duration-300',
          canSort && 'cursor-pointer'
        )}
        data-testid="data-table-head"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseDown={handleSortClick}
        style={{
          background: isHovered ? 'hsl(var(--popover-hover))' : 'initial',
        }}
      >
        <div className="flex flex-row items-center gap-x-2 truncate">
          {header.isPlaceholder ? null : flexRender(headerContent, header.getContext())}
          <SortIndicator header={header} isHovered={isHovered} />
        </div>
        {/* Filter trigger - renders when filterType is configured */}
        {columnConfig?.filterType && (
          <FilterTrigger
            header={header}
            filterType={columnConfig.filterType}
            filterOptions={columnConfig.filterOptions}
            filterPlaceholder={columnConfig.filterPlaceholder}
          />
        )}
      </div>
      <ResizeHandle header={header} resetSize={header.column.resetSize} />
    </TableHead>
  );
};

// =============================================================================
// Data Table Head
// =============================================================================

export const DataTableHead = <TData,>({
  headerGroups,
  stickyHeader,
}: DataTableHeadProps<TData>): ReactNode => {
  return (
    <TableHeader
      className={cn('grid', stickyHeader && 'z-[30]')}
      style={{
        position: stickyHeader ? 'sticky' : 'relative',
        top: stickyHeader ? 0 : 'initial',
      }}
    >
      {headerGroups.map((headerGroup) => (
        <TableRow key={headerGroup.id} className="w-full flex">
          {headerGroup.headers.map((header) => (
            <HeaderCell key={header.id} header={header} />
          ))}
        </TableRow>
      ))}
    </TableHeader>
  );
};

DataTableHead.displayName = 'DataTable.DataTableHead';
